// ABOUTME: One grey dashboard section in the builder — holds rows, or two resizable/draggable divided halves.
// ABOUTME: The whole section is a dnd-kit sortable (drag the grip to reorder it within its container).
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";
import cx from 'classnames';
import { useDroppable } from "@dnd-kit/core";
import { useSortable, SortableContext, horizontalListSortingStrategy, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RhDragBar } from "./RhDragBar";
import { ReportBuilderRow } from "./ReportBuilderRow";
import { ReportBuilderDividedSection } from "./ReportBuilderDividedSection";
import { dashboardCellKinds, dashboardPivotVariants } from "../dashboardEnums";

// Page-level sections (vs. tab sections) cannot hold a FULL pivot — a full pivot must own a whole tab.
const PAGE_SECTIONS = "sections";

export const ReportBuilderSection = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // A section holding a full pivot can't be divided (would shrink it below full width) and is LOCKED —
    // it owns its whole tab and may never be dragged out.
    const hasFullPivot = (() => {
        let found = false;
        const visit = (node) => {
            if (found || !node || typeof node !== "object") return;
            if (node.type === "column" && node.chart?.kind === dashboardCellKinds.PIVOT && node.chart?.variant === dashboardPivotVariants.FULL) { found = true; return; }
            if (Array.isArray(node.children)) node.children.forEach(visit);
        };
        visit(props.section);
        return found;
    })();

    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props.section.id,
        data: { type: "section", containerKey: props.containerKey, sectionIndex: props.si },
        // A full pivot can't be dragged, but it stays a drop TARGET so a section can be dropped above it
        // (even in a tab that holds only the pivot).
        disabled: { draggable: hasFullPivot, droppable: false },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    // Drop zone for an EMPTY, undivided section so a row can be dropped back into it (no sibling row to land
    // next to). Inactive while the section has rows or is divided (its halves carry their own zones).
    const { setNodeRef: setRowZoneRef } = useDroppable({
        id: `rowzone-${props.section.id}`,
        data: { type: "rowContainer", containerKey: props.containerKey, sectionIndex: props.si, dividerIndex: null },
        disabled: props.section.divided || props.section.children.length > 0,
    });

    // A FULL pivot owns everything below it, so it must be the LAST section of a tab body (regular sections may
    // sit above it) and be undivided + single-column. Page sections are never eligible.
    const sectionEligibleForFull =
        props.containerKey !== PAGE_SECTIONS && props.si === props.tabSectionCount - 1 && !props.section.divided && props.section.children.length === 1;

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                position:"relative",
                display:"flex",
                flexDirection:'column',
                gap:2,
            }}
        >
            {/* Top accent bar = grab to move this section. Sits ABOVE the rows (high z) so the section's top
                edge stays grabbable; right-inset past the divide button. Locked for a full pivot. */}
            {!hasFullPivot &&
                <RhDragBar
                    activatorRef={setActivatorNodeRef}
                    attributes={attributes}
                    listeners={listeners}
                    right={0}
                    zIndex={20}
                />
            }
            <Box
                sx={{
                    display:"flex",
                    gap:2,
                }}
            >
                <Box
                    sx={{
                        flex:1,
                        display:"flex",
                        flexDirection:'column',
                        gap:2,
                    }}
                >
                    <Box
                        ref={setRowZoneRef}
                        className={cx(undefined,{
                            'not-divided' : !props.section.divided,
                        })}
                        sx={{
                            display:"flex",
                            gap:2,
                            width:1,
                            minHeight: props.section.children.length === 0 ? 120 : undefined,
                            "&.not-divided": {
                                flexDirection:"column",
                                p:1.5,
                                pb:3,
                                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                borderRadius:4,
                            },
                        }}
                    >

                        {props.section.divided ? (
                            <SortableContext
                                items={props.section.children.map(d => d.id)}
                                strategy={horizontalListSortingStrategy}
                            >
                                {props.section.children.map((divSec, di) => (
                                    <ReportBuilderDividedSection
                                        key={divSec.id}
                                        di={di}
                                        si={props.si}
                                        divSec={divSec}
                                        divSecPath={`${props.sectionPath}.children.${di}`}
                                        containerKey={props.containerKey}

                                        handleRemoveRow={props.handleRemoveRow}
                                        handleAddColumn={props.handleAddColumn}
                                        handleRemoveColumn={props.handleRemoveColumn}
                                        handleResizeColumn={props.handleResizeColumn}
                                        handleRemoveDividedSection={props.handleRemoveDividedSection}
                                        handleResizeDividedSection={props.handleResizeDividedSection}
                                        handleAddRow={props.handleAddRow}

                                        onOpenReportPicker={props.onOpenReportPicker}
                                        handleSetColumnChart={props.handleSetColumnChart}
                                    />
                                ))}
                            </SortableContext>
                        ) : (
                            <SortableContext
                                items={props.section.children.map(r => r.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {props.section.children.map((row, ri) => (
                                    <ReportBuilderRow
                                        key={row.id}
                                        ri={ri}
                                        si={props.si}
                                        row={row}
                                        rowPath={`${props.sectionPath}.children.${ri}`}
                                        containerKey={props.containerKey}

                                        handleRemoveRow={props.handleRemoveRow}
                                        handleAddColumn={props.handleAddColumn}
                                        handleRemoveColumn={props.handleRemoveColumn}
                                        handleResizeColumn={props.handleResizeColumn}
                                        handleRemoveDividedSection={props.handleRemoveDividedSection}

                                        onOpenReportPicker={props.onOpenReportPicker}
                                        handleSetColumnChart={props.handleSetColumnChart}
                                        eligibleForFull={sectionEligibleForFull}
                                    />
                                ))}
                            </SortableContext>
                        )}
                        {!props.section.divided && !hasFullPivot &&
                            <ButtonBase
                                className="add-row-btn"
                                onClick={() => props.handleAddRow(props.containerKey, props.si)}
                                sx={{
                                    width:1,
                                    height:32,
                                    borderRadius:4,
                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                    "&:hover": {
                                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                    }
                                }}
                            >
                                <Text>{t("add_row")}</Text>
                            </ButtonBase>
                        }
                    </Box>

                </Box>
                {!props.section.divided && !hasFullPivot &&

                    <ButtonBase
                        className="divide-section-btn"
                        onClick={() => props.handleDivideSection(props.containerKey, props.si)}
                        sx={{
                            minHeight:200,
                            width:32,
                            borderRadius:4,
                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                            color:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                        }}
                    >
                        +
                    </ButtonBase>
                }
            </Box>

            <ButtonBase
                className="remove-section-from-tab-btn"
                onClick={() => props.handleRemoveSection(props.containerKey, props.si)}
                sx={{
                    width:1,
                    height:32,
                    borderRadius:4,
                    background:colors.red[100],
                    color:colors.red[400],
                    "&:hover" : {
                        background:colors.red[200],
                    }
                }}
            >
                {t("remove_section")}
            </ButtonBase>
        </Box>
    );
};
